import { Box, Collapse, Dialog, Divider, Slide, Tooltip } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import React, { useEffect, useRef } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ActionAreaBtn } from './ActionAreaFab';
import { AskAIBox } from './AskAIBox';
import { AskAIChat } from './AskAIChat';
import { AskAIConversationsButton } from './AskAIConversationsButton';
import { DemoPresetsBlock, WelcomeBlock } from './WelcomeBlock';
import { AiConversationWithMessages, AskAiMessage } from '../services/ai';

const SlideUp = React.forwardRef(function SlideUp(
	props: TransitionProps & { children: React.ReactElement },
	ref: React.Ref<unknown>,
) {
	return <Slide direction='up' ref={ref} {...props} />;
});

export type ChatFlowMode = 'welcome' | 'docked';

type ChatFlowProps = {
	open: boolean;
	mode: ChatFlowMode;
	isChatOpen: boolean;
	onClose: () => void;
	onCloseChat: () => void;
	isAuthenticated: boolean;
	onOpenConversation: (conversation: AiConversationWithMessages) => void;
	chatDraft: string;
	setChatDraft: (draft: string) => void;
	setIsAiSearchFocused: (focused: boolean) => void;
	isAskAiInProgress: boolean;
	handleChatSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
	messages: AskAiMessage[];
};

export const ChatFlow = ({ open, mode: currentMode, isChatOpen: currentIsChatOpen, onClose, onCloseChat, isAuthenticated, onOpenConversation, chatDraft, setChatDraft, setIsAiSearchFocused, isAskAiInProgress, handleChatSubmit, messages }: ChatFlowProps) => {
	const inputRef = useRef<HTMLInputElement>(null);
	// Closing also changes what is shown, so keep the last open layout while the dialog slides out.
	const shownRef = useRef({ mode: currentMode, isChatOpen: currentIsChatOpen });
	if (open) shownRef.current = { mode: currentMode, isChatOpen: currentIsChatOpen };
	const { mode, isChatOpen } = shownRef.current;
	const isDocked = mode === 'docked' && !isChatOpen;
	const isWelcome = mode === 'welcome' && !isChatOpen;

	useEffect(() => {
		inputRef.current?.focus();
	}, [isChatOpen, isAuthenticated]);

	const submitChat = (event: React.FormEvent<HTMLFormElement>) => {
		handleChatSubmit(event);
		inputRef.current?.focus();
	};

	return (
		<Dialog
			open={open}
			onClose={(_event, reason) => {
				if (isChatOpen) onCloseChat();
				else if (reason !== 'backdropClick') onClose();
			}}
			TransitionComponent={SlideUp}
			fullWidth
			maxWidth='sm'
			scroll='body'
			hideBackdrop={isDocked}
			disableEnforceFocus={isDocked}
			disableScrollLock={isDocked}
			// Docked at the bottom the dialog is non-modal: the canvas behind it stays interactive.
			sx={{ pointerEvents: isDocked ? 'none' : 'auto' }}
			PaperProps={{
				onClick: (event: React.MouseEvent) => event.stopPropagation(),
				onMouseDown: (event: React.MouseEvent) => event.stopPropagation(),
				sx: {
					pointerEvents: 'auto',
					bgcolor: 'transparent',
					backgroundImage: 'none',
					boxShadow: 'none',
					border: 'none',
					backdropFilter: 'none',
					WebkitBackdropFilter: 'none',
					mb: isDocked ? 5 : 4,
					display: 'inline-flex',
					flexDirection: 'column',
					verticalAlign: isDocked ? 'bottom' : 'middle',
				},
			}}
		>
			<Collapse in={isWelcome} sx={{ flexShrink: 0 }}>
				<Box display='flex' flexDirection='column' pb={1}>
					<WelcomeBlock />
				</Box>
			</Collapse>
			<Box sx={{ flexShrink: 0, display: 'flex', flexDirection: {xs: 'column', md: 'row-reverse'}, alignItems: 'center', justifyContent: 'center', gap: 1 }}>
				{isDocked && (
					<Box display='flex' alignItems='center' gap={1}>
						<AskAIConversationsButton isAuthenticated={isAuthenticated} onOpenConversation={onOpenConversation} />
						<Tooltip title='Hide' disableInteractive>
							<ActionAreaBtn onClick={onClose} aria-label='Hide Ask AI'>
								<ExpandMoreIcon />
							</ActionAreaBtn>
						</Tooltip>
					</Box>
				)}
				<AskAIBox chatDraft={chatDraft} setChatDraft={setChatDraft} setIsAiSearchFocused={setIsAiSearchFocused} isAskAiInProgress={isAskAiInProgress} handleChatSubmit={submitChat} isAuthenticated={isAuthenticated} placeholder={isWelcome ? 'What\'s your role?' : isChatOpen ? 'Reply…' : 'How can I assist?'} inputRef={inputRef}>
					<Collapse in={isChatOpen} unmountOnExit>
						<AskAIChat messages={messages} isAskAiInProgress={isAskAiInProgress} onClose={onCloseChat} />
					</Collapse>
				</AskAIBox>
			</Box>
			<Collapse in={isWelcome} sx={{ flexShrink: 0 }}>
				<Box pt={1}>
					<Divider sx={{ my: 1 }}>or</Divider>
					<DemoPresetsBlock />
				</Box>
			</Collapse>
		</Dialog>
	);
};
